<template>
  <div>
    <div
      class="mb-3"
    >
      <e-button
        :busy="loadingRequisitions"
        :text="$t('Associar Requisição de Compra')"
        variant="primary"
        icon="plus"
        @click="openSidebar"
      />
    </div>

    <b-table
      show-empty
      responsive
      striped
      class="bordered"
      :empty-text="$t('Nenhuma requisição de compra associada')"
      :fields="fields"
      :items="purchaseOrder.purchaseRequisitions"
    >
      <template #cell(purchaseRequisitionId)="row">
        <router-link
          :to="{ name: 'purchase-requisition-maintain', params: { id: row.item.purchaseRequisitionId } }"
          target="_blank"
        >
          {{ row.item.purchaseRequisitionId }}
        </router-link>
      </template>
      <template #cell(status)="row">
        <e-status-badge
          v-if="row.item.status"
          :status="row.item.status"
          :options="mxPurchaseRequisitionStatusOptions()"
        />
        <small
          v-else
          class="text-muted"
        >-</small>
      </template>

      <template #cell(productsDisplay)="row">
        <div v-if="row.item.commonItems && row.item.commonItems.length > 0">
          <small>
            <div
              v-for="(item, idx) in row.item.commonItems"
              :key="idx"
            >
              {{ formatRequisitionItem(item) }}
            </div>
          </small>
        </div>
        <small
          v-else
          class="text-muted"
        >{{ $t('Nenhum produto em comum') }}</small>
      </template>

      <template #cell(supplierOrders)="row">
        <div v-if="row.item.supplierOrders && row.item.supplierOrders.length > 0">
          <small>
            <div
              v-for="(order, idx) in row.item.supplierOrders"
              :key="idx"
            >
              {{ formatSupplierOrder(order) }}
            </div>
          </small>
        </div>
        <small
          v-else
          class="text-muted"
        >{{ $t('Nenhum pedido ao fornecedor') }}</small>
      </template>

      <template #cell(action)="row">
        <e-grid-actions
          :show-update="false"
          :show-delete="true"
          @delete="onRemoveRequisition(row.index)"
        />
      </template>
    </b-table>

    <e-sidebar
      id="sidebar-associate-requisitions"
      :title="$t('Requisições de Compra Disponíveis')"
      :show="showSidebar"
      :fetching="loadingRequisitions"
      width="1100px"
      :confirm-button-text="$t('Associar')"
      @save="onAssociateRequisitions"
      @hidden="onSidebarHidden"
    >
      <template #content>
        <div
          v-if="searchResults.length === 0"
          class="alert alert-info"
        >
          {{ $t('Não existe nenhuma requisição de compra que tenha algum produto do pedido que ainda não foi finalizada') }}
        </div>

        <template v-else>
          <div class="d-flex justify-content-end align-items-center mb-1">
            <b-badge
              :variant="selectedRequisitions.length > 0 ? 'primary' : 'secondary'"
              class="px-1 py-50"
            >
              {{ selectionSummary }}
            </b-badge>
          </div>

          <b-table
            ref="availableRequisitionsTable"
            show-empty
            responsive
            striped
            hover
            class="bordered selectable-requisitions"
            :empty-text="$t('Nenhuma requisição encontrada')"
            :fields="searchFields"
            :items="searchResults"
            selectable
            select-mode="multi"
            selected-variant="primary"
            @row-selected="onRowSelected"
          >
            <template #cell(status)="row">
              <e-status-badge
                :status="row.item.status"
                :options="mxPurchaseRequisitionStatusOptions()"
              />
            </template>

            <template #cell(matchedItems)="row">
              <div v-if="row.item.matchedItems.length > 0">
                <small>
                  <div
                    v-for="(item, idx) in row.item.matchedItems"
                    :key="idx"
                  >
                    {{ formatRequisitionItem(item) }}
                  </div>
                </small>
              </div>
              <small
                v-else
                class="text-muted"
              >{{ $t('Nenhum produto em comum') }}</small>
            </template>

            <template #cell(supplierOrders)="row">
              <div v-if="row.item.supplierOrders && row.item.supplierOrders.length > 0">
                <small>
                  <div
                    v-for="(order, idx) in row.item.supplierOrders"
                    :key="idx"
                  >
                    {{ formatSupplierOrder(order) }}
                  </div>
                </small>
              </div>
              <small
                v-else
                class="text-muted"
              >{{ $t('Nenhum pedido ao fornecedor') }}</small>
            </template>
          </b-table>
        </template>
      </template>
    </e-sidebar>
  </div>
</template>

<script>
import axios from '@axios'
import { BTable, BBadge } from 'bootstrap-vue'
import { EButton, EGridActions, EStatusBadge, ESidebar } from '@/views/components'
import { purchaseRequisitionDomains } from '@/mixins'

export default {
  name: 'PurchaseOrderRequisitions',

  components: {
    BTable,
    BBadge,
    EButton,
    EGridActions,
    EStatusBadge,
    ESidebar,
  },

  mixins: [purchaseRequisitionDomains],

  props: {
    purchaseOrder: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showSidebar: false,
      loadingRequisitions: false,
      searchResults: [],
      selectedRequisitions: [],
    }
  },

  computed: {
    fields() {
      return [
        {
          key: 'purchaseRequisitionId',
          label: this.$t('Id da Requisição'),
          sortable: false,
          class: 'text-center',
          thStyle: { width: '120px' },
        },
        {
          key: 'purchaseRequisitionDate',
          label: this.$t('Data'),
          sortable: false,
          class: 'text-center',
          thStyle: { width: '110px' },
          formatter: value => this.$options.filters.date(value),
        },
        {
          key: 'status',
          label: this.$t('Status'),
          sortable: false,
          thStyle: { width: '160px' },
        },
        {
          key: 'productsDisplay',
          label: this.$t('Produtos também no pedido'),
          sortable: false,
        },
        {
          key: 'supplierOrders',
          label: this.$t('Pedidos ao fornecedor'),
          sortable: false,
          thStyle: { width: '280px' },
        },
        {
          key: 'action',
          label: this.$t('Ações'),
          class: 'text-center',
          thStyle: { width: '90px' },
        },
      ]
    },
    searchFields() {
      return [
        { key: 'id', label: this.$t('Id'), sortable: false, thStyle: { width: '60px' } },
        { key: 'status', label: this.$t('Status'), sortable: false, thStyle: { width: '150px' } },
        {
          key: 'totalItems',
          label: this.$t('Qtd Itens'),
          sortable: false,
          class: 'text-center',
          thStyle: { width: '90px' },
        },
        {
          key: 'matchedItems',
          label: this.$t('Produtos também no pedido'),
          sortable: false,
          thStyle: { width: '280px' },
        },
        {
          key: 'supplierOrders',
          label: this.$t('Pedidos ao fornecedor'),
          sortable: false,
          thStyle: { width: '280px' },
        },
        { key: 'observation', label: this.$t('Observação'), sortable: false },
      ]
    },

    selectionSummary() {
      return `${this.selectedRequisitions.length} ${this.$t('de')} ${this.searchResults.length} ${this.$t('requisições selecionadas')}`
    },

    /**
     * SKUs presentes no pedido de compra, usados para destacar quais produtos
     * da requisição são relevantes para este pedido.
     */
    purchaseOrderSkuIds() {
      return (this.purchaseOrder.items || [])
        .map(item => item.erpSku?.sku?.id)
        .filter(id => id)
    },
  },

  methods: {
    async openSidebar() {
      this.selectedRequisitions = []
      this.searchResults = []
      this.showSidebar = true

      try {
        this.loadingRequisitions = true
        await this.loadAvailableRequisitions()
      } finally {
        this.loadingRequisitions = false
      }
    },

    closeSidebar() {
      this.showSidebar = false
    },

    onSidebarHidden() {
      this.selectedRequisitions = []
      this.showSidebar = false
    },

    async loadAvailableRequisitions() {
      try {
        const storeId = this.purchaseOrder.store?.id
        if (!storeId) {
          this.showWarning({ message: this.$t('Loja não selecionada') })
          return
        }

        // Get SKU IDs from purchase order items
        const skuIds = this.purchaseOrderSkuIds

        if (skuIds.length === 0) {
          this.showWarning({ message: this.$t('Nenhum produto no pedido de compra') })
          return
        }

        // Non-final statuses
        const nonFinalStatuses = [
          // 'Draft',
          // 'Submitted',
          // 'Approved',
          // 'ChangesRequested',
          // 'Rejected',
          // 'SubmitToSupplier',
          'SubmitedToSupplier',
        ]

        // // Build params with multiple skuId values
        // const params = {
        //   storeId,
        //   pageSize: 1000,
        //   statusList: nonFinalStatuses,
        // }

        // Create query string manually to support multiple skuId parameters
        const queryParts = []
        queryParts.push(`storeId=${storeId}`)
        queryParts.push(`pageSize=1000`)
        queryParts.push(`includeItems=true`)
        nonFinalStatuses.forEach(status => {
          queryParts.push(`statusList=${status}`)
        })
        skuIds.forEach(skuId => {
          queryParts.push(`skuIds=${skuId}`)
        })
        const queryString = queryParts.join('&')

        const { data } = await axios.get(`/api/purchase-requisitions?${queryString}`)

        // Destaca somente os itens da requisição que também estão no pedido de compra
        this.searchResults = (data.results || []).map(requisition => ({
          ...requisition,
          matchedItems: (requisition.items || []).filter(item =>
            skuIds.includes(item.skuId ?? item.sku?.id)
          ),
        }))
      } catch (error) {
        this.showError({ error, message: this.$t('Erro ao buscar requisições de compra') })
        this.searchResults = []
      }
    },

    onRowSelected(items) {
      this.selectedRequisitions = items
    },

    onAssociateRequisitions() {
      if (this.selectedRequisitions.length === 0) {
        this.showWarning({ message: this.$t('Nenhuma requisição selecionada') })
        return
      }

      if (!this.purchaseOrder.purchaseRequisitions) {
        this.$set(this.purchaseOrder, 'purchaseRequisitions', [])
      }

      // Add selected requisitions avoiding duplicates.
      // Os dados da requisição são copiados para que a tabela já exiba as informações
      // completas antes do pedido ser salvo e recarregado.
      this.selectedRequisitions.forEach(requisition => {
        if (!this.isAlreadyAssociated(requisition.id)) {
          this.purchaseOrder.purchaseRequisitions.push({
            id: null,
            purchaseOrderId: this.purchaseOrder.id,
            purchaseRequisitionId: requisition.id,
            purchaseRequisitionDate: requisition.requestedDate,
            status: requisition.status,
            supplierOrders: requisition.supplierOrders || [],
            commonItems: requisition.matchedItems || [],
          })
        }
      })

      this.showSuccess({ message: this.$t('Requisições associadas com sucesso') })
      this.$emit('requisitions-changed')
      this.closeSidebar()
    },

    isAlreadyAssociated(requisitionId) {
      return (this.purchaseOrder.purchaseRequisitions || []).some(
        r => r.purchaseRequisitionId === requisitionId
      )
    },

    onRemoveRequisition(index) {
      this.purchaseOrder.purchaseRequisitions.splice(index, 1)
      this.$emit('requisitions-changed')
    },

    getProductName(item) {
      if (item && item.sku) {
        return item.sku.name || item.sku.id
      }
      return 'Produto desconhecido'
    },

    formatRequisitionItem(item) {
      const quantity = this.$options.filters.number(item.quantity, 3, true)
      return `${quantity} x ${this.getProductName(item)}`
    },

    formatSupplierOrder(order) {
      return [
        this.$options.filters.date(order.requestedDate),
        order.orderNumber ? `${this.$t('Pedido')} ${order.orderNumber}` : null,
        order.supplier,
      ]
        .filter(part => part)
        .join(' - ')
    },
  },
}
</script>

<style lang="scss">
/**
 * Destaque das linhas selecionadas. O tema neutraliza o realce padrão do
 * b-table (.b-table-row-selected.table-active recebe a mesma cor do hover),
 * por isso o destaque é definido explicitamente aqui.
 */
.selectable-requisitions {
  tbody tr {
    cursor: pointer;
  }

  tbody tr.b-table-row-selected > td {
    background-color: rgba(56, 146, 83, 0.18) !important;
  }

  tbody tr.b-table-row-selected > td:first-child {
    box-shadow: inset 4px 0 0 0 #67f0ab;
    font-weight: 600;
  }
}
</style>
